import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { fetchTatListAction } from "../../actions/fetchTatListAction";
import "./index.scss";

const TatHistory = () => {
  const [tatData, setTatData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 10;

  useEffect(() => {
    loadTatData();
  }, [currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTatData = async () => {
    setLoading(true);
    try {
      const result = await fetchTatListAction({
        pageNumber: currentPage,
        pageSize,
      });

      if (result.success) {
        setTatData(result.data || []);
        setTotalCount(result.totalCount || 0);
      } else {
        toast.error(result.message || "Failed to load TAT history");
        setTatData([]);
        setTotalCount(0);
      }
    } catch (error) {
      toast.error("Failed to load TAT history");
      setTatData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);
  const startEntry = (currentPage - 1) * pageSize + 1;
  const endEntry = Math.min(currentPage * pageSize, totalCount);

  // Format date to DD-MM-YYYY HH:MM:SS
  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const seconds = date.getSeconds().toString().padStart(2, "0");
    return `${day}-${month}-${year}\n${hours}:${minutes}:${seconds}`;
  };

  // Get status badge class
  const getStatusClass = (status) => {
    if (!status) return "tat-status--default";
    const statusLower = status.toLowerCase();
    if (statusLower.includes("approved")) return "tat-status--approved";
    if (statusLower.includes("vendor")) return "tat-status--vendor";
    if (statusLower.includes("authorizer")) return "tat-status--authorizer";
    return "tat-status--default";
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 7;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) pages.push(i);
        pages.push("...");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push("...");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push("...");
        pages.push(totalPages);
      }
    }
    return pages;
  };

  return (
    <div className="tat-history">
      <div className="tat-history__header">
        <div className="tat-history__header-content">
          <h1 className="tat-history__title">TAT History</h1>
          <p className="tat-history__subtitle">
            Track turnaround time for vendor onboarding process
          </p>
        </div>
        <div className="tat-history__stats">
          <div className="tat-stat-card">
            <div className="tat-stat-card__value">{totalCount}</div>
            <div className="tat-stat-card__label">Total Entities</div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="tat-loading">
          <div className="tat-loading__spinner"></div>
          <p className="tat-loading__text">Loading TAT history...</p>
        </div>
      ) : tatData.length === 0 ? (
        <div className="tat-empty">
          <div className="tat-empty__icon">📊</div>
          <h3 className="tat-empty__title">No TAT Data Found</h3>
          <p className="tat-empty__text">
            There are no TAT records available at the moment.
          </p>
        </div>
      ) : (
        <>
          <div className="tat-table-container">
            <div className="tat-table-wrapper">
              <table className="tat-table">
                <thead>
                  <tr>
                    <th>Entity ID</th>
                    <th>Entity Name</th>
                    <th>Link Shared to vendor</th>
                    <th>Documents uploaded</th>
                    <th>Verified On</th>
                    <th>Current Status</th>
                  </tr>
                </thead>
                <tbody>
                  {tatData.map((item, index) => (
                    <tr key={item.entityId || index}>
                      <td>
                        <span className="tat-entity-id">{item.entityId}</span>
                      </td>
                      <td>
                        <span className="tat-entity-name">
                          {item.entityName}
                        </span>
                      </td>
                      <td>
                        <div className="tat-date">
                          {formatDate(item.linkSharedToVendor)}
                        </div>
                      </td>
                      <td>
                        <div className="tat-date">
                          {formatDate(item.documentsUploadedOn)}
                        </div>
                      </td>
                      <td>
                        <div className="tat-date">
                          {formatDate(item.verifiedOn)}
                        </div>
                      </td>
                      <td>
                        <span
                          className={`tat-status ${getStatusClass(item.currentStatus)}`}
                        >
                          {item.currentStatus || "—"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="tat-pagination">
            <div className="tat-pagination__info">
              <span className="tat-pagination__text">
                Showing <strong>{startEntry}</strong> to{" "}
                <strong>{endEntry}</strong> of <strong>{totalCount}</strong>{" "}
                entries
              </span>
            </div>
            <div className="tat-pagination__controls">
              <button
                className="tat-pagination__btn tat-pagination__btn--nav"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(1)}
                title="First page"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="currentColor"
                >
                  <path d="M11 2L5 8l6 6V2z" />
                  <path d="M5 2v12" />
                </svg>
              </button>
              <button
                className="tat-pagination__btn tat-pagination__btn--nav"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                title="Previous page"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="currentColor"
                >
                  <path d="M10 2L4 8l6 6V2z" />
                </svg>
              </button>

              {getPageNumbers().map((page, idx) =>
                page === "..." ? (
                  <span
                    key={`ellipsis-${idx}`}
                    className="tat-pagination__ellipsis"
                  >
                    ⋯
                  </span>
                ) : (
                  <button
                    key={page}
                    className={`tat-pagination__btn ${page === currentPage ? "tat-pagination__btn--active" : ""}`}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </button>
                ),
              )}

              <button
                className="tat-pagination__btn tat-pagination__btn--nav"
                disabled={currentPage === totalPages}
                onClick={() =>
                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                }
                title="Next page"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="currentColor"
                >
                  <path d="M6 2l6 6-6 6V2z" />
                </svg>
              </button>
              <button
                className="tat-pagination__btn tat-pagination__btn--nav"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(totalPages)}
                title="Last page"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="currentColor"
                >
                  <path d="M5 2l6 6-6 6V2z" />
                  <path d="M11 2v12" />
                </svg>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TatHistory;
